import React, { useState } from 'react';
import { FiMapPin, FiPhone, FiMail, FiClock, FiMessageCircle, FiSend } from 'react-icons/fi';
import { motion } from 'framer-motion';
import FadeInView from './FadeInView';
import { PRIMARY_API_URL } from '../config/api';

const ease = [0.22, 1, 0.36, 1];

const contactInfo = [
  {
    icon: FiPhone,
    title: "Phone",
    details: ["+30 2663098633", "24/7 Customer Service"],
    action: null,
    actionText: null
  },
  {
    icon: FiMail,
    title: "Email",
    details: ["contact@lasmari.gr", "Business Inquiries"],
    action: null,
    actionText: null
  },
  {
    icon: FiMessageCircle,
    title: "WhatsApp",
    details: ["+44 7404 560313", "Instant Booking"],
    action: "https://wa.me/447404560313",
    actionText: "Message Us"
  },
  {
    icon: FiMapPin,
    title: "Service Area",
    details: ["Corfu, Greece", "Base, Kassiopi"],
    action: null,
    actionText: null
  }
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${PRIMARY_API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus({
          type: 'success',
          message: result.message || 'Thank you for your message! We\'ll get back to you within 24 hours.'
        });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        let errorMessage = result.error || 'Sorry, there was an error sending your message. Please try again or contact us directly.';
        if (result.details && Array.isArray(result.details)) {
          const validationErrors = result.details.map(detail => detail.msg).join(', ');
          errorMessage = `Validation failed: ${validationErrors}`;
        }
        setSubmitStatus({ type: 'error', message: errorMessage });
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again or contact us directly.'
      });
    }

    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-[#faf7f6]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInView
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Reach Out</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Get In <span className="italic">Touch</span>
          </h2>
        </FadeInView>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          {/* Contact Information */}
          <FadeInView
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
          >
            <p className="text-[#5b4d4f] text-sm font-light leading-relaxed mb-8">
              Our team is available 24/7 to assist with your transportation needs.
              Whether you're planning ahead or need immediate service, we're just a call away.
            </p>

            <div className="space-y-4">
              {contactInfo.map((info, i) => (
                <FadeInView
                  key={info.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.02, ease }}
                  className="group bg-white border border-black/10 rounded-xl p-5 shadow-sm hover:border-[#ba1609]/30 hover:shadow-md transition-[color,background-color,border-color,box-shadow] duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#fbeceb] border border-[#ba1609]/20 flex items-center justify-center shrink-0 group-hover:bg-[#ba1609]/15 transition-colors duration-500">
                      <info.icon className="w-4 h-4 text-[#ba1609]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-[#1a1012] font-medium text-sm mb-1">{info.title}</h4>
                      {info.details.map((detail, j) => (
                        <p key={j} className={j === 0 ? 'text-[#5b4d4f] text-sm' : 'text-[#9a8e90] text-xs'}>{detail}</p>
                      ))}
                      {info.action && (
                        <a
                          href={info.action}
                          target={info.action.startsWith('http') ? '_blank' : undefined}
                          rel={info.action.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="inline-flex items-center text-xs text-[#ba1609] hover:text-[#8e1107] transition-colors mt-1"
                        >
                          {info.actionText}
                          <FiSend className="w-3 h-3 ml-1.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </FadeInView>
              ))}
            </div>

            {/* Operating Hours */}
            <FadeInView
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3, ease }}
              className="mt-4 bg-white border border-black/10 rounded-xl p-5 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <FiClock className="w-4 h-4 text-[#ba1609]" />
                <h4 className="text-[#1a1012] font-medium text-sm">Operating Hours</h4>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#5b4d4f] font-light">Emergency Service:</span>
                  <span className="text-[#1a1012] font-medium">24/7</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5b4d4f] font-light">Customer Support:</span>
                  <span className="text-[#1a1012] font-medium">24/7</span>
                </div>
              </div>
            </FadeInView>
          </FadeInView>

          {/* Contact Form */}
          <FadeInView
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
            className="bg-white border border-black/10 rounded-2xl p-8 lg:p-10 shadow-sm"
          >
            <h3 className="text-xl font-medium text-[#1a1012] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Send a Message
            </h3>
            <div className="w-10 h-[1px] bg-[#ba1609] mb-6" />
            <p className="text-[#5b4d4f] text-sm font-light mb-8">
              Have a specific question or special request? We'll respond promptly.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-[#5b4d4f] uppercase tracking-wider mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    minLength={2}
                    maxLength={100}
                    pattern="[a-zA-Z\s'-]+"
                    className="w-full px-4 py-3 bg-white border border-black/10 rounded-lg text-[#1a1012] placeholder-[#9a8e90] focus:outline-none focus:ring-1 focus:ring-[#ba1609]/40 focus:border-[#ba1609]/40 transition-all text-sm"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#5b4d4f] uppercase tracking-wider mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-black/10 rounded-lg text-[#1a1012] placeholder-[#9a8e90] focus:outline-none focus:ring-1 focus:ring-[#ba1609]/40 focus:border-[#ba1609]/40 transition-all text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-[#5b4d4f] uppercase tracking-wider mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    pattern="[\+]?[\d\s\-\(\)]{0,20}"
                    className="w-full px-4 py-3 bg-white border border-black/10 rounded-lg text-[#1a1012] placeholder-[#9a8e90] focus:outline-none focus:ring-1 focus:ring-[#ba1609]/40 focus:border-[#ba1609]/40 transition-all text-sm"
                    placeholder="+30 6XX XXX XXXX"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#5b4d4f] uppercase tracking-wider mb-2">Subject</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-black/10 rounded-lg text-[#1a1012] focus:outline-none focus:ring-1 focus:ring-[#ba1609]/40 focus:border-[#ba1609]/40 transition-all text-sm"
                  >
                    <option value="">Select a subject</option>
                    <option value="booking">Booking Inquiry</option>
                    <option value="quote">Price Quote</option>
                    <option value="service">Service Information</option>
                    <option value="support">Customer Support</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs text-[#5b4d4f] uppercase tracking-wider mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={5}
                  className="w-full px-4 py-3 bg-white border border-black/10 rounded-lg text-[#1a1012] placeholder-[#9a8e90] focus:outline-none focus:ring-1 focus:ring-[#ba1609]/40 focus:border-[#ba1609]/40 transition-all resize-vertical text-sm"
                  placeholder="Tell us about your transportation needs..."
                />
              </div>

              {submitStatus.message && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg border text-sm ${
                    submitStatus.type === 'success'
                      ? 'bg-green-50 border-green-500/30 text-green-700'
                      : 'bg-[#fbeceb] border-[#ba1609]/30 text-[#8e1107]'
                  }`}
                >
                  {submitStatus.message}
                </motion.div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-[#ba1609] text-white font-medium rounded-full hover:bg-[#8e1107] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm tracking-wide"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FiSend className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </FadeInView>
        </div>
      </div>
    </section>
  );
};

export default Contact;
